// Plan -> geometry + colliders.
//
// Everything is merged by material into a handful of meshes. A dense street is thousands of boxes;
// as individual Mesh objects that is thousands of draw calls and the frame-time gate fails before
// System 3 ever gets to add a light.
//
// System 1 scope: FORM ONLY. Materials here are deliberately plain -- flat colour, no maps. System 2
// replaces the whole palette with procedural bakes. If a critic's tell is about surface rather than
// shape, that is a System 2 item and gets logged, not fixed here.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { rngFor, fbm2 } from '../rng.js';
import { bakeAll, applyWorldWear } from '../materials/bake.js';
import { makeWindowMaterial, paneAttr } from '../materials/window.js';
import { buildSignAtlas, makeSignMaterial, cellsOfKind, flickerFor } from '../signage.js';
import { vehicleFromPool, tintPaint, PAINTS } from '../geometry/vehicles.js';
import { placeParts, creaseNormals } from '../geometry/procedural.js';
import { WORLD, WALKWAY_Y } from '../config.js';
import { ALLEY_W, ALLEY_E, ARCADE, WALKWAY } from './layout.js';

// ---------------------------------------------------------------------------
// Placeholder palette (System 2 replaces this wholesale)
// ---------------------------------------------------------------------------
// Built once, shared by every sign in the city.
export const SIGN_ATLAS = { tex: null, cells: null };
let SIGN_MAT = null;

export function makeMaterials() {
  if (!SIGN_ATLAS.tex) {
    const a = buildSignAtlas();
    SIGN_ATLAS.tex = a.tex; SIGN_ATLAS.cells = a.cells;
    SIGN_MAT = makeSignMaterial(a.tex);
  }
  const M = (color, roughness, metalness, extra = {}) =>
    new THREE.MeshStandardMaterial({ color, roughness, metalness, ...extra });

  // System 2. Every map here is computed at boot from a field function in materials/bake.js -- no
  // image files, nothing fetched. `metres` travels with the material so Builder.worldUVs projects
  // at exactly the scale the field was authored at; the material and its UV scale cannot disagree.
  const T = bakeAll();
  const S = (baked, tint, extra = {}) => {
    const m = new THREE.MeshStandardMaterial({
      color: tint || 0xffffff,
      map: baked.map,
      roughnessMap: baked.rm,
      metalnessMap: baked.rm,   // roughness in G, metalness in B, same texel, same sample
      normalMap: baked.nm,
      roughness: 1.0,
      metalness: 1.0,
      ...extra,
    });
    m.normalScale = new THREE.Vector2(1.0, 1.0);
    m.userData.metres = baked.metres;
    // World-space wear: gravity streaking, contact grime, traffic polish. A baked tile cannot know
    // where on the building it landed, so these three terms have to be applied in world space.
    applyWorldWear(m);
    return m;
  };

  return {
    asphalt: S(T.asphalt),
    pavement: S(T.pavement),
    kerb: S(T.kerb),
    concrete: S(T.concrete),
    concreteB: S(T.concrete, 0xc8ccd4),
    brick: S(T.brick),
    darkMetal: S(T.rusted, 0x9099a8),
    steel: S(T.steel),
    painted: S(T.painted),
    paintedWarm: S(T.painted, 0xd8a07a),
    rusted: S(T.rusted),
    tile: S(T.tile),
    // Overhaul 3A slots. paint carries per-vehicle tint as VERTEX COLORS -- one welded bucket,
    // per-car colour; a material per car would be a draw call per car.
    paint: new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.34, metalness: 0.72 }),
    chrome: M(0x9aa4ae, 0.22, 0.95),
    lensRed: new THREE.MeshStandardMaterial({
      color: 0x180a0a, emissive: 0xff2a1a, emissiveIntensity: 1.6, roughness: 0.3,
    }),
    vTrim: M(0x54575c, 0.48, 0.90),
    vLiner: M(0x1c1e22, 0.60, 0.40),
    // envMapIntensity 2.4: car glass at night reads by REFLECTING the signs above it (critic fix
    // #2 -- "the strongest single night-time car cue"); at the scene's 0.42 env level it was
    // invisible dark-on-dark and the greenhouse vanished into the body wedge.
    vGlass: Object.assign(M(0x0c1016, 0.10, 0.65), { envMapIntensity: 2.4 }),
    vRubber: M(0x17181a, 0.94, 0.0),
    vLens: new THREE.MeshStandardMaterial({
      color: 0x141414, emissive: 0xffe8c0, emissiveIntensity: 1.4, roughness: 0.25,
    }),
    window: makeWindowMaterial(),
    sign: SIGN_MAT,
    glassDark: M(0x0a0d12, 0.16, 0.55),
    glassLit: new THREE.MeshStandardMaterial({
      color: 0x14181f, roughness: 0.2, metalness: 0.3,
      emissive: 0xffdaa8, emissiveIntensity: 0.55,
    }),
    glassLitCool: new THREE.MeshStandardMaterial({
      color: 0x101820, roughness: 0.2, metalness: 0.3,
      emissive: 0x9fd8ff, emissiveIntensity: 0.42,
    }),
    grate: S(T.grate, 0xb8bec8, { transparent: false }),
    cable: M(0x101114, 0.85, 0.15),
    plastic: S(T.plastic),
    backdrop: M(0x17181c, 0.95, 0.0),
    backdropLit: new THREE.MeshStandardMaterial({
      color: 0x16171b, roughness: 0.9, metalness: 0.0,
      emissive: 0x2a2f3a, emissiveIntensity: 0.5,
    }),
  };
}

// ---------------------------------------------------------------------------
// Geometry accumulator
// ---------------------------------------------------------------------------
class Builder {
  constructor(materials) {
    this.mats = materials;
    this.buckets = new Map(); // matKey -> [BufferGeometry]
    this.count = 0;
    // System 3: light sources are emitted BY the geometry that carries the fitting, so a lamp and
    // the light it casts can never end up in different places. A critic reported light pools "with
    // no fixture anywhere in frame" -- that happens when the two are authored separately.
    this.sources = [];
    this.vents = [];   // steam emitters, registered by whatever builds the vent
    this.grounded = []; // {x,z,y,r} for contact-shadow decals
  }

  /**
   * Add toolkit parts to the city buckets. Slots that map onto existing city materials reuse
   * those buckets (draw calls must NOT increase -- the weld-law gate); only paint, chrome and
   * lensRed are new. Each part gets crease normals HERE, per part, because the city's finish()
   * never recomputes normals and ExtrudeGeometry's own are flat facets.
   */
  addParts(parts) {
    // Vehicle slots get their OWN buckets: the city's primitive buckets are INDEXED geometry and
    // toolkit parts are de-indexed by design, and mergeGeometries refuses a mixed list (it failed
    // at index 1929 of darkMetal when these were shared). The vehicle buckets are all
    // non-shadow-casting, which keeps total draws under the pre-overhaul baseline.
    const MAP = { trim: 'vTrim', liner: 'vLiner', glassCar: 'vGlass', rubber: 'vRubber', lens: 'vLens' };
    for (const p of parts) {
      creaseNormals(p.geo, 40);
      this.add(MAP[p.slot] || p.slot, p.geo);
    }
  }

  /** Register a light source at a fitting. kind drives intensity/range/halo size in main.js. */
  source(kind, x, y, z, color, gain = 1) {
    this.sources.push({ kind, pos: [x, y, z], color, gain });
  }

  add(matKey, geo) {
    if (!this.buckets.has(matKey)) this.buckets.set(matKey, []);
    this.buckets.get(matKey).push(geo);
    this.count++;
  }

  /** Axis-aligned box by CENTRE. */
  box(matKey, cx, cy, cz, w, h, d, rotY = 0) {
    const g = new THREE.BoxGeometry(w, h, d);
    const m = new THREE.Matrix4();
    if (rotY) m.makeRotationY(rotY);
    m.setPosition(cx, cy, cz);
    g.applyMatrix4(m);
    this.add(matKey, g);
    return g;
  }

  /** Box by EXTENTS -- usually clearer for architecture. */
  span(matKey, x0, x1, y0, y1, z0, z1) {
    return this.box(matKey, (x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2,
      Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0));
  }

  cyl(matKey, cx, cy, cz, rTop, rBot, h, seg = 8, rotZ = 0, rotY = 0) {
    const g = new THREE.CylinderGeometry(rTop, rBot, h, seg);
    const e = new THREE.Euler(0, rotY, rotZ);
    const m = new THREE.Matrix4().makeRotationFromEuler(e);
    m.setPosition(cx, cy, cz);
    g.applyMatrix4(m);
    this.add(matKey, g);
    return g;
  }

  tube(matKey, curve, rad, tubular = 14, radial = 5) {
    this.add(matKey, new THREE.TubeGeometry(curve, tubular, rad, radial, false));
  }

  /**
   * A sign face: one quad indexed into the shared sign atlas.
   *
   * Signs are the most numerous emissive object on the street, so they all share one material and
   * one texture; a material per sign would be sixty extra draw calls. Per-sign behaviour rides on
   * `aSign = (flickerPhase, flickerKind, holoFlag)`.
   *
   * Placing a sign ALSO registers a light source, through the same path the lamps use -- the
   * System 3 critic's routing note was that lit signs are both the missing subject and the missing
   * light source, so the two are one record here and cannot drift apart.
   */
  signQuad(x0, x1, y0, y1, z0, z1, cell, key, gain = 1, facing = 1) {
    const w = Math.abs(x1 - x0), hh = Math.abs(y1 - y0), d = Math.abs(z1 - z0);
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, cz = (z0 + z1) / 2;
    let g;
    // `facing` is the direction the sign is meant to be READ from. PlaneGeometry's front face is
    // +Z; a sign hung to be read from -Z is the same quad seen from behind, and with DoubleSide the
    // text comes out MIRRORED. A critic caught three arcade signs reading backwards. Flipping U for
    // back-facing quads is the fix -- and it has to be decided here, from the geometry, not left to
    // whoever places the sign to remember.
    let flipU = false;
    if (d <= w && d <= hh) {
      g = new THREE.PlaneGeometry(w, hh);
      flipU = facing < 0;
    } else if (w <= d && w <= hh) {
      g = new THREE.PlaneGeometry(d, hh);
      g.rotateY(Math.PI / 2);
      flipU = facing < 0;
    } else {
      g = new THREE.PlaneGeometry(w, d);
      g.rotateX(-Math.PI / 2);
    }
    g.translate(cx, cy, cz);

    const uv = g.attributes.uv;
    for (let i = 0; i < uv.count; i++) {
      const u = flipU ? 1 - uv.getX(i) : uv.getX(i);
      uv.setXY(i, cell.u0 + u * (cell.u1 - cell.u0),
        cell.v0 + uv.getY(i) * (cell.v1 - cell.v0));
    }
    const n = g.attributes.position.count;
    const a = new Float32Array(n * 3);
    const ph = ((cell.i * 37) % 101) / 101;
    const fk = flickerFor(key);
    const holo = cell.kind === 'holo' ? 1 : 0;
    for (let i = 0; i < n; i++) { a[i * 3] = ph; a[i * 3 + 1] = fk; a[i * 3 + 2] = holo; }
    g.setAttribute('aSign', new THREE.BufferAttribute(a, 3));
    this.add('sign', g);
    this.source('sign', cx, cy, cz, cell.color, gain);
    return g;
  }

  /**
   * A window pane, carrying a PER-INSTANCE identity.
   *
   * The interior of a lit window cannot come from a tiled texture -- a tile would repeat the same
   * room in every opening, which reads as clones rather than as a building full of people. Each
   * pane gets `aPane = (hash, lit, temp)` and the shader synthesises its interior from that in
   * pane-local UV space. BoxGeometry already gives each face a 0..1 UV with V running vertically on
   * the large faces of a thin pane, so no extra UV work is needed -- and worldUVs must NOT run on
   * this bucket, which it does not, because the window material carries no `metres`.
   */
  pane(matKey, x0, x1, y0, y1, z0, z1, key) {
    const g = new THREE.BoxGeometry(Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0));
    g.translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
    const a = paneAttr(key);
    const n = g.attributes.position.count;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { arr[i * 3] = a[0]; arr[i * 3 + 1] = a[1]; arr[i * 3 + 2] = a[2]; }
    g.setAttribute('aPane', new THREE.BufferAttribute(arr, 3));
    this.add(matKey, g);
    return g;
  }

  /**
   * A cylinder spanning two points. Use this for ANY member that is not axis-aligned.
   *
   * Added round 6. `cyl()` takes (rotZ, rotY) and a Three.js cylinder's axis is +Y, so `rotY` alone
   * is a NO-OP -- Ry(theta) maps (0,1,0) to itself for every theta. Seven call sites passed
   * (0, PI/2) intending a horizontal run and silently got a vertical stick: every alley conduit,
   * the arcade ceiling conduit, fire-escape cross rails, stair landing rails, walkway mesh infill,
   * and every vehicle wheel. Nothing errored. Angles derived by hand are the same trap one step
   * further on, which is why stair rails and braces now go through here instead.
   */
  rod(matKey, a, b, rad, seg = 6) {
    const A = new THREE.Vector3(...a), Bv = new THREE.Vector3(...b);
    const dir = Bv.clone().sub(A);
    const len = dir.length();
    if (len < 1e-6) return;
    const g = new THREE.CylinderGeometry(rad, rad, len, seg);
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    const m = new THREE.Matrix4().makeRotationFromQuaternion(q);
    m.setPosition(A.clone().add(Bv).multiplyScalar(0.5));
    g.applyMatrix4(m);
    this.add(matKey, g);
  }

  /**
   * Replace the geometry's UVs with a WORLD-SPACE planar projection, chosen per vertex by the
   * dominant axis of its normal.
   *
   * BoxGeometry gives every face a 0..1 UV, so a tiled texture would stretch one tile across a 20 m
   * wall and squash it onto a 0.05 m bracket -- every surface would show its texture at a different
   * scale, which reads worse than no texture at all. Projecting from world position means one
   * texture tile is always the same number of real metres regardless of how big the box is, and
   * adjacent boxes line up because they share the same projection.
   *
   * Safe here because essentially all of this city is axis-aligned. `rod()` members are the
   * exception and are thin enough that the projection is not readable on them.
   */
  static worldUVs(geo, metres) {
    const pos = geo.attributes.position, nor = geo.attributes.normal;
    const uv = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
      let u, v;
      if (ny >= nx && ny >= nz) { u = x; v = z; }        // floor / ceiling
      else if (nx >= nz) { u = z; v = y; }               // wall facing +-X
      else { u = x; v = y; }                             // wall facing +-Z
      uv[i * 2] = u / metres;
      uv[i * 2 + 1] = v / metres;
    }
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  }

  finish(scene) {
    const meshes = [];
    for (const [key, geos] of this.buckets) {
      if (!geos.length) continue;
      const merged = mergeGeometries(geos, false);
      if (!merged) {
        console.error(`[build] merge failed for material bucket "${key}" (${geos.length} geoms)`);
        continue;
      }
      const mat = this.mats[key];
      if (mat && mat.userData && mat.userData.metres) {
        Builder.worldUVs(merged, mat.userData.metres);
      }
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, this.mats[key]);
      mesh.name = `city.${key}`;
      // Draw budget: each casting bucket renders into every shadow pass (~13 with two point
      // lights + the key). Vehicle paint/chrome/lenses and the overhead cables contribute nothing
      // readable to night shadow maps, so they buy back the budget the three new buckets cost --
      // net draws end BELOW the pre-overhaul baseline, which is what a correct weld should do.
      mesh.castShadow = !['paint', 'chrome', 'lensRed', 'cable', 'vTrim', 'vLiner', 'vGlass', 'vRubber', 'vLens'].includes(key);
      mesh.receiveShadow = true;
      scene.add(mesh);
      meshes.push(mesh);
      for (const g of geos) g.dispose();
    }
    return meshes;
  }
}

// ---------------------------------------------------------------------------
// Ground: cambered roadway, kerbed sidewalks
// ---------------------------------------------------------------------------
function buildGround(B, world) {
  const { ROAD_HALF, FACE, STREET_Z0, STREET_Z1, KERB_H, ROAD_CROWN } = WORLD;

  // Roadway as a displaced grid so the camber is real geometry, not a normal-map lie. Puddles in
  // System 3 need somewhere to actually pool; a flat plane gives them nowhere to go.
  const nx = 24, nz = 200;
  const road = new THREE.PlaneGeometry(ROAD_HALF * 2, STREET_Z1 - STREET_Z0, nx, nz);
  road.rotateX(-Math.PI / 2);
  road.translate(0, 0, (STREET_Z0 + STREET_Z1) / 2);
  {
    const p = road.attributes.position;
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), z = p.getZ(i);
      const t = Math.abs(x) / ROAD_HALF;
      // Camber + low-frequency settlement. Real asphalt is never flat: it sags between patches.
      const settle = (fbm2(x * 0.09, z * 0.09, 3) - 0.5) * 0.055;
      p.setY(i, ROAD_CROWN * (1 - t * t) + settle);
    }
    road.computeVertexNormals();
  }
  B.add('asphalt', road);

  for (const sign of [-1, 1]) {
    // Sidewalk slab
    B.span('pavement', sign * ROAD_HALF, sign * FACE, 0, KERB_H, STREET_Z0, STREET_Z1);
    // Kerb face -- a distinct strip. The kerb line is the single strongest scale cue at street level.
    B.span('kerb', sign * ROAD_HALF, sign * (ROAD_HALF + 0.14), 0, KERB_H + 0.005, STREET_Z0, STREET_Z1);
  }

  // Ground beyond the street ends, so the horizon is not a void.
  B.span('asphalt', -60, 60, -0.02, 0, STREET_Z1, STREET_Z1 + 70);
  B.span('asphalt', -60, 60, -0.02, 0, STREET_Z0 - 70, STREET_Z0);

  // Alley + arcade floors, slightly below sidewalk with a centre channel for drainage.
  for (const a of [ALLEY_W, ALLEY_E]) {
    B.span('concreteB', a.x0, a.x1, 0, KERB_H - 0.03, a.z0, a.z1);
    const zc = (a.z0 + a.z1) / 2;
    B.span('grate', a.x0, a.x1, KERB_H - 0.055, KERB_H - 0.028, zc - 0.11, zc + 0.11);
  }
  B.span('pavement', ARCADE.x0, ARCADE.x1, 0, KERB_H, ARCADE.z0, ARCADE.z1);

  world.floor(ALLEY_W.x0, ALLEY_W.x1, ALLEY_W.z0, ALLEY_W.z1, KERB_H - 0.03, 'alleyW');
  world.floor(ALLEY_E.x0, ALLEY_E.x1, ALLEY_E.z0, ALLEY_E.z1, KERB_H - 0.03, 'alleyE');
  world.floor(ARCADE.x0, ARCADE.x1, ARCADE.z0, ARCADE.z1, KERB_H, 'arcade');
}

// ---------------------------------------------------------------------------
// One building parcel
// ---------------------------------------------------------------------------
function buildParcel(B, world, p) {
  const r = rngFor(`${p.key}.geo`);
  const { z0, z1, faceX, backX, h, sign } = p;

  // TWO DIRECTIONS, NAMED, DERIVED ONCE.
  //
  // Round 3 fix. This function previously carried `const inward = -sign`, which is backwards:
  // faceX = sign*(FACE+setback) and backX = sign*(FACE+DEPTH), so |backX| > |faceX| and travelling
  // INTO the block means travelling along +sign. Both directions were inverted and every consumer
  // used the wrong one -- windows protruded 0.13 m into the street instead of recessing, and the
  // entire facade-detail layer (AC condensers, downpipes, sign brackets, awnings, fire escapes) was
  // built INSIDE the solid wall where nothing could ever see it. Roof clutter floated over the
  // roadway.
  //
  // A blind critic reported "zero facade relief, no downpipes, no condensers, no awnings, rooflines
  // terminate in a knife edge" on a build that generates all of them. The geometry was never
  // missing; it was hidden. Never spell these two directions inline again.
  const IN = sign;    // face -> block interior
  const OUT = -sign;  // face -> out over the pavement

  // FENCE. Checked against faceX and backX -- facts about where this building actually stands --
  // not against the expression that produced IN and OUT. The strong form of a fence requires the
  // measurement and the config to be computed independently, so flipping the definition above makes
  // this throw rather than quietly hiding another facade inside a wall.
  if (Math.sign(backX - faceX) !== Math.sign(IN)) {
    throw new Error(`[build] ${p.key}: IN does not point face->back (faceX ${faceX}, backX ${backX}, IN ${IN})`);
  }
  if (Math.abs(faceX + IN * 0.5) <= Math.abs(faceX) || Math.abs(faceX + OUT * 0.5) >= Math.abs(faceX)) {
    throw new Error(`[build] ${p.key}: IN/OUT inverted about the street centreline (faceX ${faceX}, IN ${IN})`);
  }

  // --- main mass -----------------------------------------------------------
  //
  // Round 4. The mass used to run faceX -> backX as ONE SOLID BOX, and windows were placed by
  // putting a glass pane at a depth inside it. That cannot work: a pane 0.20 m inside solid concrete
  // is invisible. Round 3 "correctly" recessed the glass and thereby deleted every window in the
  // city -- a critic reported "every building is a closed solid, not one window opening" and was
  // exactly right.
  //
  // A punched-window facade is not a solid with panes in it. It is a SKIN WITH VOIDS: the structural
  // core is set back by SKIN, and the outer layer is built only where wall actually exists --
  // spandrel bands between floors, piers between openings. The holes are what is left over.
  const SKIN = 0.30;
  const coreX = faceX + IN * SKIN;
  const recX = faceX + IN * p.shopRecess;   // back of the ground-floor shopfront recess
  const bodyMat = r.weighted(['concrete', 'concreteB', 'brick'], [0.45, 0.35, 0.20]);

  // The core is split at the shopfront head. The ground storey is set back further than the floors
  // above it, so a single 0..h core would fill the shopfront recess with solid mass -- the same
  // defect as the windows, one storey down.
  //
  // It is also held back a further SHOP_DEPTH so there is an actual interior volume behind the
  // glazing. Glass with solid wall immediately behind it reads as a lit panel, not a shop; you need
  // somewhere for the eye to travel into.
  const SHOP_DEPTH = 1.5;
  const shopBackX = recX + IN * SHOP_DEPTH;
  B.span(bodyMat, shopBackX, backX, 0, p.shopH, z0, z1);
  B.span(bodyMat, coreX, backX, p.shopH, h, z0, z1);
  // Side walls of the shop interior.
  B.span(bodyMat, recX, shopBackX, 0, p.shopH, z0, z0 + 0.12);
  B.span(bodyMat, recX, shopBackX, 0, p.shopH, z1 - 0.12, z1);
  // Something in the shop, so the interior is not an empty box: a counter and a back shelf run.
  B.span('paintedWarm', shopBackX + OUT * 0.55, shopBackX + OUT * 0.15, 0, 0.95, z0 + 0.5, z1 - 0.5);
  for (let sy = 1.15; sy < p.shopH - 0.6; sy += 0.62) {
    B.span('darkMetal', shopBackX + OUT * 0.30, shopBackX, sy, sy + 0.05, z0 + 0.3, z1 - 0.3);
  }
  // Collider still spans the full footprint -- the reveal is 0.30 m deep and not walkable.
  world.solid(Math.min(faceX, backX), Math.max(faceX, backX), z0, z1, 0, h, p.key);

  // Optional material change partway up, standing slightly proud (a re-clad / later addition).
  if (p.bandY) {
    const other = bodyMat === 'concrete' ? 'concreteB' : 'concrete';
    B.span(other, faceX, faceX + OUT * 0.07, p.bandY, h, z0 + 0.02, z1 - 0.02);
  }

  // --- ground-floor shopfront: recessed glazing under a fascia -------------
  // Side reveals of the recess
  B.span(bodyMat, faceX, recX, 0, p.shopH, z0, z0 + 0.35);
  B.span(bodyMat, faceX, recX, 0, p.shopH, z1 - 0.35, z1);
  // Fascia above the shopfront, projecting proud of the face -- the heavy band that separates
  // street from building, and the thing that throws a shadow down over the glazing.
  B.span('concreteB', faceX + OUT * 0.09, recX, p.shopH, p.shopH + 0.62, z0, z1);
  // Soffit of the recess
  B.span(bodyMat, faceX, recX, p.shopH - 0.06, p.shopH, z0, z1);
  // String course above the shopfront, running the full frontage.
  B.span('concreteB', faceX + OUT * 0.15, faceX, p.shopH + 0.62, p.shopH + 0.80, z0, z1);

  // Glazing + mullions across the recess
  const units = Math.max(2, Math.round((z1 - z0 - 0.7) / r.range(1.5, 2.3)));
  const uw = (z1 - z0 - 0.7) / units;
  for (let u = 0; u < units; u++) {
    const a = z0 + 0.35 + u * uw, b = a + uw;
    r.chance(0.66); r.chance(0.72);   // keep the draw sequence stable
    B.pane('window', recX, recX + IN * 0.05, 0.22, p.shopH - 0.30, a + 0.055, b - 0.055,
      `${p.key}.shop.${u}`);
    // Full-height pier between tenancies
    B.span('darkMetal', recX + OUT * 0.04, recX + IN * 0.06, 0.18, p.shopH - 0.22, b - 0.07, b + 0.03);
    // Stall riser below the glass
    B.span('darkMetal', recX + OUT * 0.03, recX + IN * 0.08, 0, 0.22, a, b);
  }
  // Head rail
  B.span('darkMetal', recX + OUT * 0.03, recX + IN * 0.07, p.shopH - 0.30, p.shopH - 0.20, z0 + 0.3, z1 - 0.3);
  // A recessed entrance door, deeper than the glazing line, breaking the ribbon.
  {
    const dz = z0 + r.range(0.25, 0.75) * (z1 - z0 - 1.0);
    const dX = recX + IN * 0.32;
    B.span(bodyMat, recX, dX, 0, 2.32, dz - 0.10, dz + 0.02);
    B.span(bodyMat, recX, dX, 0, 2.32, dz + 1.00, dz + 1.12);
    B.span(bodyMat, recX, dX, 2.20, 2.32, dz, dz + 1.02);
    B.span('glassDark', dX, dX + IN * 0.05, 0.05, 2.20, dz + 0.03, dz + 0.99);
  }
  world.solid(Math.min(faceX, recX), Math.max(faceX, recX), z0, z1, 0, p.shopH, `${p.key}.shop`);

  // --- SIGNAGE (System 4) --------------------------------------------------
  // The fascia carcass and the sign brackets were built in System 1 with nothing on them. Both are
  // filled here, and each one becomes a light source in the same call.
  {
    const cells = SIGN_ATLAS.cells;
    const fascias = cellsOfKind(cells, 'fascia').concat(cellsOfKind(cells, 'box'));
    const blades = cellsOfKind(cells, 'blade').concat(cellsOfKind(cells, 'neon'));
    const rs = rngFor(p.key + '.sign');

    if (rs.chance(0.86) && fascias.length) {
      const c = rs.pick(fascias);
      const inset = Math.min(0.55, (z1 - z0) * 0.10);
      B.signQuad(faceX + OUT * 0.115, faceX + OUT * 0.125,
        p.shopH + 0.07, p.shopH + 0.55, z0 + inset, z1 - inset, c, p.key + '.fascia', 1.25, OUT);
    }
    for (let i = 0; i < p.brackets; i++) {
      const rr = rngFor(p.key + '.brk.' + i);
      const y = rr.range(p.shopH + 0.9, Math.min(h - 1.2, p.shopH + 9.0));
      const zc = z0 + rr.range(0.15, 0.85) * (z1 - z0);
      const out = rr.range(0.7, 2.1);
      if (!blades.length || !rs.chance(0.82)) continue;
      const c = rs.pick(blades);
      const tall = c.kind === 'blade';
      const sw = tall ? out * 0.62 : out * 0.92;
      const sh = tall ? rs.range(1.5, 2.6) : rs.range(0.62, 0.95);
      B.signQuad(faceX + OUT * (out - sw), faceX + OUT * out,
        y - sh, y, zc - 0.035, zc + 0.035, c, p.key + '.blade.' + i, 1.0);
      B.span('steel', faceX + OUT * (out - sw), faceX + OUT * out, y, y + 0.06, zc - 0.05, zc + 0.05);
    }

    // UPPER-FACADE SIGNS. A critic: "nothing exists above floor two... that single band is the
    // giveaway." Real dense streets carry signage all the way up the elevation -- tall vertical
    // blades bolted to the wall between window columns, and wide banners across the spandrels.
    const upper = cellsOfKind(cells, 'blade').concat(cellsOfKind(cells, 'neon'), cellsOfKind(cells, 'holo'));
    const nUp = rs.int(1, 4);
    for (let i = 0; i < nUp && upper.length; i++) {
      const c = rs.pick(upper);
      const zc = z0 + rs.range(0.12, 0.88) * (z1 - z0);
      const tall = rs.chance(0.55);
      if (tall) {
        // Vertical blade running up several storeys, standing proud of the wall on a frame.
        const yb = p.shopH + rs.range(1.4, 4.0);
        const sh = rs.range(2.6, Math.max(3.0, Math.min(9.0, h - yb - 1.2)));
        const sw2 = rs.range(0.85, 1.5);
        B.signQuad(faceX + OUT * 0.30, faceX + OUT * 0.315, yb, yb + sh,
          zc - sw2 / 2, zc + sw2 / 2, c, p.key + '.up.' + i, 1.15, OUT);
        B.span('darkMetal', faceX, faceX + OUT * 0.30, yb - 0.06, yb + sh + 0.06,
          zc - sw2 / 2 - 0.05, zc - sw2 / 2);
        B.span('darkMetal', faceX, faceX + OUT * 0.30, yb - 0.06, yb + sh + 0.06,
          zc + sw2 / 2, zc + sw2 / 2 + 0.05);
      } else {
        // Wide banner across a spandrel band.
        const yb = p.shopH + rs.range(2.2, Math.max(2.6, h - p.shopH - 3.0));
        const sh = rs.range(0.7, 1.35);
        const sw2 = Math.min((z1 - z0) * 0.8, rs.range(2.4, 5.0));
        B.signQuad(faceX + OUT * 0.18, faceX + OUT * 0.19, yb, yb + sh,
          zc - sw2 / 2, zc + sw2 / 2, c, p.key + '.up.' + i, 0.95, OUT);
        B.span('darkMetal', faceX, faceX + OUT * 0.18, yb - 0.07, yb, zc - sw2 / 2, zc + sw2 / 2);
      }
    }
  }

  // Shopfront spill. Placed just outside the glazing this parcel actually built, at its actual
  // head height -- so the light lands on the pavement the shopfront is standing over.
  {
    const zc = (z0 + z1) / 2;
    const warm = r.chance(0.66);
    B.source('shopfront', faceX + OUT * 0.9, p.shopH * 0.52, zc,
      warm ? 0xffb066 : 0x8fd0ff, r.range(0.75, 1.25));
    // A second, weaker one washing the facade above the fascia.
    B.source('facade', faceX + OUT * 1.5, p.shopH + 2.2, zc, warm ? 0xffa860 : 0x86c4ff, 0.42);
  }

  // --- awning over the shopfront ------------------------------------------
  if (p.awning) {
    const out = r.range(1.1, 2.0);
    const yTop = p.shopH + 0.30, yLip = yTop - p.awningDrop * 0.42;
    const ax0 = faceX, ax1 = faceX + OUT * out;
    B.span('paintedWarm', ax0, ax1, yLip, yLip + 0.07, z0 + 0.15, z1 - 0.15);
    B.span('paintedWarm', ax1, ax1 + OUT * 0.03, yLip - 0.34, yLip + 0.07, z0 + 0.15, z1 - 0.15);
    for (let z = z0 + 0.5; z < z1 - 0.4; z += r.range(1.6, 2.6)) {
      B.cyl('steel', (ax0 + ax1) / 2, yTop - 0.1, z, 0.022, 0.022, out * 1.05, 6, Math.PI / 2, 0);
    }
    world.solid(Math.min(ax0, ax1), Math.max(ax0, ax1), z0, z1, yLip - 0.4, yLip + 0.1, `${p.key}.awning`);
  }

  // --- upper-floor windows -------------------------------------------------
  //
  // A facade is a shadow machine. The reveal depth is what makes a wall read as solid mass rather
  // than a coloured plane, and it does that with the materials and lights exactly as they are --
  // which is why this is System 1 work and not something to defer to System 2.
  const cols = p.windowCols;
  const colW = (z1 - z0) / cols;
  const winW = colW * r.range(0.46, 0.66);
  const winH = p.floorH * r.range(0.56, 0.70);

  let ySkin = p.shopH + 0.80;   // top of the string course; skin is built upward from here
  for (let f = 0; f < p.floors; f++) {
    const yBase = p.shopH + 0.80 + f * p.floorH;
    const yTop = yBase + winH;
    if (yTop + 0.45 > h) break;

    // Spandrel: full-width band of skin between the last opening row and this one.
    B.span(bodyMat, faceX, coreX, ySkin, yBase, z0, z1);

    // Within the opening band, build PIERS between openings. The gaps are the windows.
    let zCursor = z0;
    for (let c = 0; c < cols; c++) {
      const zc = z0 + (c + 0.5) * colW;
      const wz0 = zc - winW / 2, wz1 = zc + winW / 2;
      const rr = rngFor(`${p.key}.w.${f}.${c}`);

      // A blanked / bricked-up opening: leave the skin solid across it, so the grid is not perfect.
      if (rr.chance(0.07)) continue;

      B.span(bodyMat, faceX, coreX, yBase, yTop, zCursor, wz0);
      zCursor = wz1;

      // Glass sits on the face of the CORE, at the back of a genuine 0.30 m reveal.
      // Lit-ness, colour temperature and interior layout all come from the pane's own hash.
      B.pane('window', coreX, coreX + IN * 0.04, yBase, yTop, wz0, wz1, `${p.key}.w.${f}.${c}`);
      rr.chance(0.32);   // keep this key's draw sequence stable for downstream decisions
      // Frame and one vertical mullion, set just proud of the glass inside the reveal.
      B.span('darkMetal', coreX + OUT * 0.05, coreX, yBase, yBase + 0.05, wz0, wz1);
      B.span('darkMetal', coreX + OUT * 0.05, coreX, yTop - 0.05, yTop, wz0, wz1);
      B.span('darkMetal', coreX + OUT * 0.05, coreX, yBase, yTop, (wz0 + wz1) / 2 - 0.025, (wz0 + wz1) / 2 + 0.025);

      // Sill, projecting 0.06 proud of the facade plane.
      B.span('concreteB', faceX + OUT * 0.06, faceX, yBase - 0.06, yBase, wz0 - 0.09, wz1 + 0.09);
      // Head band, projecting slightly, throwing a shadow down into the reveal.
      B.span('concreteB', faceX + OUT * 0.03, faceX, yTop, yTop + 0.10, wz0 - 0.07, wz1 + 0.07);

      // Occasional AC box hung under an opening -- lived-in, not designed. Sized to a real
      // split-system outdoor unit (0.85 x 0.62 x 0.32), not the oversized box that was here.
      if (rr.chance(0.18)) {
        B.span('plastic', faceX, faceX + OUT * 0.32, yBase - 0.72, yBase - 0.10, zc - 0.425, zc + 0.425);
        B.span('grate', faceX + OUT * 0.32, faceX + OUT * 0.34, yBase - 0.66, yBase - 0.16, zc - 0.36, zc + 0.36);
        B.span('steel', faceX, faceX + OUT * 0.32, yBase - 0.80, yBase - 0.72, zc - 0.38, zc - 0.32);
        B.span('steel', faceX, faceX + OUT * 0.32, yBase - 0.80, yBase - 0.72, zc + 0.32, zc + 0.38);
      }
    }
    B.span(bodyMat, faceX, coreX, yBase, yTop, zCursor, z1);
    ySkin = yTop;
  }
  // Skin from the last opening row up to the roof.
  B.span(bodyMat, faceX, coreX, ySkin, h, z0, z1);
  // Skin either side of the whole opening zone, closing the corners onto the neighbouring parcel.
  B.span(bodyMat, faceX, coreX, p.shopH, p.shopH + 0.80, z0, z1);

  // --- cornice + parapet ---------------------------------------------------
  // Projecting cornice at the roofline: 0.34 proud, 0.30 tall. Reads as a hard horizontal shadow
  // line separating building from sky, which every real masonry street has and no blockout does.
  B.span('concreteB', faceX + OUT * 0.34, faceX + IN * 0.30, h - 0.30, h, z0 - 0.05, z1 + 0.05);
  B.span('concreteB', faceX + OUT * 0.12, faceX + IN * 0.55, h, h + p.parapet, z0, z1);
  B.span('concreteB', faceX, backX, h, h + 0.18, z0, z0 + 0.35);
  B.span('concreteB', faceX, backX, h, h + 0.18, z1 - 0.35, z1);

  // --- fire escape ---------------------------------------------------------
  if (p.fireEscape) buildFireEscape(B, world, p, r);

  // --- downpipes -----------------------------------------------------------
  for (let i = 0; i < p.downpipes; i++) {
    const rr = rngFor(`${p.key}.pipe.${i}`);
    const zc = z0 + rr.range(0.35, 0.95) * (z1 - z0);
    const x = faceX + OUT * 0.09;
    B.cyl('darkMetal', x, h * 0.5, zc, 0.055, 0.055, h, 7);
    for (let y = 1.2; y < h; y += rr.range(2.2, 3.4)) {
      B.span('steel', faceX, x + OUT * 0.03, y, y + 0.05, zc - 0.09, zc + 0.09);
    }
    // Hopper at the top, shoe at the bottom.
    B.span('darkMetal', faceX, x + OUT * 0.06, h - 0.5, h - 0.18, zc - 0.14, zc + 0.14);
    B.cyl('darkMetal', x + OUT * 0.04, 0.34, zc, 0.05, 0.075, 0.5, 7, 0.22 * -sign, 0);
  }

  // --- wall-mounted AC / condenser units ------------------------------------
  for (let i = 0; i < p.ac; i++) {
    const rr = rngFor(`${p.key}.ac.${i}`);
    const y = rr.range(p.shopH + 1.4, Math.max(p.shopH + 2.0, h - 2.0));
    const zc = z0 + rr.range(0.1, 0.9) * (z1 - z0);
    const w = rr.range(0.62, 0.95), hh = rr.range(0.44, 0.68), d = rr.range(0.30, 0.44);
    B.span('plastic', faceX, faceX + OUT * d, y, y + hh, zc - w / 2, zc + w / 2);
    B.span('grate', faceX + OUT * d, faceX + OUT * (d + 0.02), y + 0.05, y + hh - 0.05,
      zc - w / 2 + 0.05, zc + w / 2 - 0.05);
    B.span('steel', faceX, faceX + OUT * d, y - 0.10, y, zc - w / 2 + 0.05, zc - w / 2 + 0.10);
    B.span('steel', faceX, faceX + OUT * d, y - 0.10, y, zc + w / 2 - 0.10, zc + w / 2 - 0.05);
  }

  // --- meter cabinets and louvre grilles at street level --------------------
  for (let i = 0; i < 2; i++) {
    const rr = rngFor(`${p.key}.meter.${i}`);
    if (!rr.chance(0.55)) continue;
    const zc = z0 + rr.range(0.1, 0.9) * (z1 - z0);
    B.span('painted', faceX, faceX + OUT * 0.20, 1.10, 1.72, zc - 0.30, zc + 0.30);
    B.span('darkMetal', faceX + OUT * 0.20, faceX + OUT * 0.22, 1.16, 1.66, zc - 0.25, zc + 0.25);
  }

  // --- sign armatures (brackets only; System 4 hangs the signs) -------------
  for (let i = 0; i < p.brackets; i++) {
    const rr = rngFor(`${p.key}.brk.${i}`);
    const y = rr.range(p.shopH + 0.9, Math.min(h - 1.2, p.shopH + 9.0));
    const zc = z0 + rr.range(0.15, 0.85) * (z1 - z0);
    const out = rr.range(0.7, 2.1);
    B.cyl('steel', faceX + OUT * out / 2, y, zc, 0.035, 0.035, out, 6, Math.PI / 2, 0);
    B.cyl('steel', faceX + OUT * out * 0.35, y - out * 0.30, zc, 0.022, 0.022, out * 0.9, 6,
      Math.PI / 2 + 0.7, 0);
    B.span('steel', faceX, faceX + OUT * 0.06, y - 0.16, y + 0.16, zc - 0.08, zc + 0.08);
  }

  // --- roof clutter ---------------------------------------------------------
  for (let i = 0; i < p.roofClutter; i++) {
    const rr = rngFor(`${p.key}.roof.${i}`);
    const kind = rr.weighted(['tank', 'vent', 'plant', 'aerial'], [0.28, 0.30, 0.26, 0.16]);
    const x = faceX + IN * rr.range(1.2, WORLD.BLOCK_DEPTH - 2.0);
    const zc = z0 + rr.range(0.15, 0.85) * (z1 - z0);
    if (kind === 'tank') {
      const rad = rr.range(0.55, 1.05), hh = rr.range(1.1, 2.0);
      B.cyl('darkMetal', x, h + hh / 2 + 0.6, zc, rad, rad, hh, 10);
      for (const dx of [-rad * 0.7, rad * 0.7]) {
        for (const dz of [-rad * 0.7, rad * 0.7]) {
          B.cyl('steel', x + dx, h + 0.3, zc + dz, 0.05, 0.05, 0.6, 5);
        }
      }
    } else if (kind === 'vent') {
      const w = rr.range(0.8, 1.6);
      B.span('steel', x - w / 2, x + w / 2, h, h + rr.range(0.5, 1.0), zc - w / 2, zc + w / 2);
      B.cyl('darkMetal', x, h + 1.15, zc, 0.28, 0.34, 0.5, 8);
    } else if (kind === 'plant') {
      const w = rr.range(1.4, 2.6), d = rr.range(1.0, 1.8), hh = rr.range(0.8, 1.5);
      B.span('painted', x - w / 2, x + w / 2, h, h + hh, zc - d / 2, zc + d / 2);
      B.span('grate', x - w / 2 + 0.1, x + w / 2 - 0.1, h + hh, h + hh + 0.04, zc - d / 2 + 0.1, zc + d / 2 - 0.1);
    } else {
      const hh = rr.range(2.2, 5.0);
      B.cyl('steel', x, h + hh / 2, zc, 0.03, 0.05, hh, 5);
      for (let k = 0; k < 3; k++) {
        B.cyl('steel', x, h + hh * (0.45 + k * 0.18), zc, 0.012, 0.012, rr.range(0.4, 0.9), 4, Math.PI / 2, rr.range(0, 3));
      }
    }
  }
}

function buildFireEscape(B, world, p, r) {
  const { z0, z1, faceX, h } = p;
  // A fire escape hangs OVER the pavement. Round 3: this function used `sign` throughout, which is
  // the into-the-block direction, so every landing and ladder was built inside the solid wall.
  const sign = -p.sign;
  const zc = z0 + r.range(0.3, 0.7) * (z1 - z0);
  const w = 2.3, out = 1.25;
  const levels = Math.min(5, Math.max(2, Math.floor((h - 4.5) / 3.1)));
  for (let L = 0; L < levels; L++) {
    const y = p.shopH + 0.9 + L * 3.1;
    if (y + 1.4 > h) break;
    // Landing deck
    B.span('grate', faceX, faceX + sign * out, y, y + 0.05, zc - w / 2, zc + w / 2);
    // Railings
    for (const dz of [-w / 2, w / 2]) {
      B.cyl('steel', faceX + sign * out * 0.5, y + 0.53, zc + dz, 0.018, 0.018, out, 5, Math.PI / 2, 0);
      B.cyl('steel', faceX + sign * out * 0.5, y + 1.02, zc + dz, 0.018, 0.018, out, 5, Math.PI / 2, 0);
    }
    B.cyl('steel', faceX + sign * out, y + 0.53, zc, 0.018, 0.018, w, 5, Math.PI / 2, Math.PI / 2);
    B.cyl('steel', faceX + sign * out, y + 1.02, zc, 0.018, 0.018, w, 5, Math.PI / 2, Math.PI / 2);
    for (const dz of [-w / 2, -w / 6, w / 6, w / 2]) {
      B.cyl('steel', faceX + sign * out, y + 0.55, zc + dz, 0.016, 0.016, 1.06, 5);
    }
    // Ladder to the next level, offset side to side
    if (L < levels - 1) {
      const lz = zc + (L % 2 ? -1 : 1) * (w / 2 - 0.35);
      const dy = 3.1;
      for (const dx of [0.16, out - 0.16]) {
        B.cyl('steel', faceX + sign * dx, y + dy / 2, lz, 0.017, 0.017, dy * 1.06, 5, 0.30 * (L % 2 ? -1 : 1), Math.PI / 2);
      }
      for (let s = 0; s < 9; s++) {
        const t = (s + 0.5) / 9;
        B.cyl('steel', faceX + sign * (out / 2), y + dy * t, lz + (t - 0.5) * 0.7 * (L % 2 ? -1 : 1),
          0.012, 0.012, out - 0.3, 4, Math.PI / 2, 0);
      }
    }
    world.solid(Math.min(faceX, faceX + sign * out), Math.max(faceX, faceX + sign * out),
      zc - w / 2, zc + w / 2, y, y + 0.06, `${p.key}.fe.${L}`);
  }
}

// ---------------------------------------------------------------------------
// Alley ends, arcade shell, stair, walkway
// ---------------------------------------------------------------------------
function buildAlleys(B, world) {
  // Back walls closing the alleys
  for (const a of [ALLEY_W, ALLEY_E]) {
    const far = a.x0 < 0 ? a.x0 : a.x1;
    const near = a.x0 < 0 ? a.x0 - 1.2 : a.x1 + 1.2;
    B.span('concreteB', far, near, 0, 14, a.z0 - 0.4, a.z1 + 0.4);
    world.solid(Math.min(far, near), Math.max(far, near), a.z0 - 0.4, a.z1 + 0.4, 0, 14, 'alleyEnd');
  }

  // Alley service dressing: conduit runs, ducts, wall lamps' housings, pipe bundles.
  for (const [name, a] of [['w', ALLEY_W], ['e', ALLEY_E]]) {
    const r = rngFor(`alley.dress.${name}`);
    const zLo = a.z0, zHi = a.z1;
    const x0 = Math.min(a.x0, a.x1), x1 = Math.max(a.x0, a.x1);
    for (const z of [zLo, zHi]) {
      const inw = z === zLo ? -1 : 1;   // outward normal direction
      // Horizontal conduit bundle
      for (let k = 0; k < 3; k++) {
        const y = r.range(2.4, 4.6);
        const len = r.range(5, 13);
        const xs = r.range(x0 + 1, x1 - len - 1);
        B.cyl('darkMetal', xs + len / 2, y, z - inw * 0.06, r.range(0.03, 0.07), r.range(0.03, 0.07), len, 6, Math.PI / 2, 0);
      }
      // Big square duct
      if (r.chance(0.8)) {
        const y = r.range(3.0, 4.8), len = r.range(4, 9), xs = r.range(x0 + 1.5, x1 - len - 1.5);
        B.span('steel', xs, xs + len, y, y + 0.55, z - inw * 0.62, z - inw * 0.06);
        for (let s = xs + 0.6; s < xs + len; s += 1.2) {
          B.span('steel', s, s + 0.05, y - 0.02, y + 0.57, z - inw * 0.64, z - inw * 0.04);
        }
      }
      // Vertical pipes
      for (let k = 0; k < 4; k++) {
        const x = r.range(x0 + 1, x1 - 1);
        B.cyl('darkMetal', x, 5, z - inw * 0.07, r.range(0.03, 0.06), r.range(0.03, 0.06), 10, 6);
      }
      // Extractor fan housing
      if (r.chance(0.7)) {
        const x = r.range(x0 + 2, x1 - 2), y = r.range(2.6, 3.6);
        B.span('steel', x - 0.5, x + 0.5, y, y + 1.0, z - inw * 0.75, z - inw * 0.05);
        B.cyl('grate', x, y + 0.5, z - inw * 0.78, 0.36, 0.36, 0.08, 12, Math.PI / 2, Math.PI / 2);
        B.vents.push({ x, y: y + 0.5, z: z - inw * 0.95, scale: 1.25 });
      }

      dressAlleyWall(B, world, x0, x1, z, inw, `${name}.${z.toFixed(1)}`);
    }
  }
}

/**
 * SYSTEM 1 AMENDMENT (logged post sign-off).
 *
 * `buildParcel` dresses only `faceX`, the street plane. An alley is the GAP between two parcels, so
 * what it looks at is undressed side faces -- and two critics running called both alley walls
 * featureless, with station 2 scoring 1.0/10 and dragging the whole set.
 *
 * The parcel mass here is solid, so openings cannot be punched the way the street facade does it.
 * Instead this builds a thin outer PLATE standing 0.14 m proud of the wall with genuine gaps in it,
 * and puts the window panes on the wall plane behind. The plate edge gives the reveal shadow; the
 * pane's own shader supplies the lit interior. Same read, a tenth of the geometry.
 */
function dressAlleyWall(B, world, x0, x1, zWall, inw, key) {
  const r = rngFor(`alley.face.${key}`);
  const OUT = -inw;                 // from the wall out into the alley
  const p = (d) => zWall + OUT * d; // a plane d metres out from the wall

  // Hopper windows: small, high, irregular. Service elevations do not carry a tidy grid.
  const cols = Math.floor((x1 - x0) / r.range(2.6, 3.6));
  for (let f = 0; f < 4; f++) {
    const yB = 3.0 + f * r.range(2.9, 3.4);
    if (yB > 15) break;
    for (let c = 0; c < cols; c++) {
      const xc = x0 + 1.4 + (c + 0.5) * ((x1 - x0 - 2.8) / cols);
      const rr = rngFor(`${key}.hop.${f}.${c}`);
      if (rr.chance(0.34)) continue;                 // many bays are blank brick
      const w = rr.range(0.72, 1.15), h = rr.range(0.62, 1.05);
      B.pane('window', xc - w / 2, xc + w / 2, yB, yB + h, p(0.0), p(0.05), `${key}.hop.${f}.${c}`);
      // Proud frame around it -- four plates leaving the opening clear.
      const t = 0.09;
      B.span('darkMetal', xc - w / 2 - t, xc + w / 2 + t, yB - t, yB, p(0.02), p(0.16));
      B.span('darkMetal', xc - w / 2 - t, xc + w / 2 + t, yB + h, yB + h + t, p(0.02), p(0.16));
      B.span('darkMetal', xc - w / 2 - t, xc - w / 2, yB, yB + h, p(0.02), p(0.16));
      B.span('darkMetal', xc + w / 2, xc + w / 2 + t, yB, yB + h, p(0.02), p(0.16));
      // Projecting sill, and a security bar on the lower openings.
      B.span('concreteB', xc - w / 2 - 0.13, xc + w / 2 + 0.13, yB - 0.10, yB - 0.04, p(0.0), p(0.22));
      if (f === 0) {
        for (let bI = 0; bI < 4; bI++) {
          B.cyl('steel', xc - w / 2 + (bI + 0.5) * (w / 4), yB + h / 2, p(0.13), 0.012, 0.012, h, 5);
        }
      }
    }
  }

  // Service doors: a steel leaf, raised threshold, small canopy, and a lamp over it.
  const doors = Math.max(1, Math.floor((x1 - x0) / 9));
  for (let d = 0; d < doors; d++) {
    const xc = x0 + 2.5 + d * ((x1 - x0 - 5) / Math.max(1, doors)) + r.jitter(1.2);
    B.span('painted', xc - 0.48, xc + 0.48, 0.0, 2.10, p(0.0), p(0.09));
    B.span('darkMetal', xc - 0.56, xc + 0.56, 2.10, 2.22, p(0.0), p(0.14));   // head
    B.span('darkMetal', xc - 0.56, xc - 0.48, 0.0, 2.10, p(0.0), p(0.14));    // jambs
    B.span('darkMetal', xc + 0.48, xc + 0.56, 0.0, 2.10, p(0.0), p(0.14));
    B.span('kerb', xc - 0.60, xc + 0.60, 0.0, 0.11, p(0.0), p(0.42));         // threshold
    B.cyl('steel', xc + 0.34, 1.06, p(0.13), 0.022, 0.022, 0.34, 6, 0, Math.PI / 2); // push bar
    B.span('steel', xc - 0.70, xc + 0.70, 2.30, 2.38, p(0.0), p(0.62));       // canopy
    B.rod('steel', [xc - 0.60, 2.94, p(0.02)], [xc - 0.60, 2.34, p(0.56)], 0.018, 5);
    B.rod('steel', [xc + 0.60, 2.94, p(0.02)], [xc + 0.60, 2.34, p(0.56)], 0.018, 5);
    // Bulkhead lamp on a bracket -- a fixture, so the light in this alley has a body.
    B.cyl('steel', xc, 3.16, p(0.16), 0.016, 0.016, 0.32, 5, Math.PI / 2, Math.PI / 2);
    B.span('darkMetal', xc - 0.14, xc + 0.14, 3.06, 3.26, p(0.28), p(0.44));
    B.span('glassLit', xc - 0.10, xc + 0.10, 3.09, 3.23, p(0.44), p(0.46));
    B.source('bulkhead', xc, 3.16, p(0.55), 0xd8e8d0, 0.7);
    world.solid(xc - 0.6, xc + 0.6, Math.min(zWall, p(0.42)), Math.max(zWall, p(0.42)), 0, 0.11, 'threshold');
  }

  // Downpipes into a shoe, meter cabinets, a string course. The things that make a blank elevation
  // read as the back of a building rather than as a wall.
  for (let i = 0; i < 3; i++) {
    const xc = r.range(x0 + 1.5, x1 - 1.5);
    B.cyl('darkMetal', xc, 7.5, p(0.08), 0.055, 0.055, 15, 7);
    for (let y = 1.4; y < 15; y += r.range(2.4, 3.2)) {
      B.span('steel', xc - 0.09, xc + 0.09, y, y + 0.05, p(0.0), p(0.11));
    }
    B.cyl('darkMetal', xc, 0.36, p(0.11), 0.05, 0.075, 0.55, 7, 0.20 * OUT, 0);
    B.span('grate', xc - 0.22, xc + 0.22, 0.0, 0.03, p(0.0), p(0.40));       // gully under the shoe
  }
  for (let i = 0; i < 2; i++) {
    if (!r.chance(0.7)) continue;
    const xc = r.range(x0 + 2, x1 - 2);
    B.span('painted', xc - 0.32, xc + 0.32, 1.05, 1.78, p(0.0), p(0.19));
    B.span('darkMetal', xc - 0.27, xc + 0.27, 1.11, 1.72, p(0.19), p(0.21));
  }
  B.span('concreteB', x0, x1, 2.42, 2.58, p(0.0), p(0.07));                   // string course
  B.span('concreteB', x0, x1, 0.0, 0.34, p(0.0), p(0.05));                    // plinth
}

function buildArcade(B, world) {
  const { x0, x1, z0, z1, ceil } = ARCADE;
  const cz0 = ARCADE.cz0, cz1 = ARCADE.cz1;   // concourse edges (shop glazing lines)

  // Ceiling slab + the building mass carried over it.
  B.span('concreteB', x0, x1, ceil, ceil + 0.45, z0, z1);
  B.span('concrete', x0, x1, ceil + 0.45, 20.0, z0, z1);
  world.solid(x0, x1, z0, z1, ceil, 20.0, 'arcade.ceil');
  // The shop bays are solid outside the concourse -- you cannot walk through a shopfront.
  world.solid(x0, x1, z0, cz0 - 0.05, 0, ceil, 'arcade.bayN');
  world.solid(x0, x1, cz1 + 0.05, z1, 0, ceil, 'arcade.bayS');

  // Far end. Round 5: this used to be a blank wall closing a 35 m arcade with ten units and no way
  // out, which a critic flagged twice. It now opens into a portal with two steps down, so the space
  // reads as a through-route rather than a dead end.
  const pz = (cz0 + cz1) / 2, pw = 1.55, ph = 3.3;
  B.span('concreteB', x1, x1 + 1.0, 0, ceil + 0.5, z0, pz - pw);
  B.span('concreteB', x1, x1 + 1.0, 0, ceil + 0.5, pz + pw, z1);
  B.span('concreteB', x1, x1 + 1.0, ph, ceil + 0.5, pz - pw, pz + pw);
  world.solid(x1, x1 + 1.0, z0, pz - pw, 0, ceil, 'arcade.back');
  world.solid(x1, x1 + 1.0, pz + pw, z1, 0, ceil, 'arcade.back');
  world.solid(x1, x1 + 1.0, pz - pw, pz + pw, ph, ceil, 'arcade.head');
  // Two steps down through the portal, and a darker space beyond so it reads as depth not a hole.
  for (let s = 0; s < 2; s++) {
    B.span('kerb', x1 + 0.30 + s * 0.34, x1 + 0.64 + s * 0.34, 0, WORLD.KERB_H - s * 0.075, pz - pw, pz + pw);
  }
  B.span('concreteB', x1 + 1.0, x1 + 5.0, 0, ph + 0.6, pz - pw - 0.8, pz - pw);
  B.span('concreteB', x1 + 1.0, x1 + 5.0, 0, ph + 0.6, pz + pw, pz + pw + 0.8);
  B.span('concreteB', x1 + 4.6, x1 + 5.0, 0, ph + 0.6, pz - pw, pz + pw);
  B.span('concreteB', x1 + 1.0, x1 + 5.0, ph + 0.2, ph + 0.6, pz - pw, pz + pw);

  // Structural bays: beams across, columns down both sides.
  const r = rngFor('arcade.struct');
  const bays = 7;
  for (let i = 0; i <= bays; i++) {
    const x = x0 + (x1 - x0) * (i / bays);
    B.span('steel', x - 0.09, x + 0.09, ceil - 0.34, ceil, cz0 + 0.1, cz1 - 0.1);
    for (const z of [cz0 + 0.28, cz1 - 0.28]) {
      B.span('concrete', x - 0.16, x + 0.16, 0, ceil - 0.30, z - 0.16, z + 0.16);
    }
  }
  // Service run along the ceiling -- ducts and conduit, never a clean soffit.
  B.span('steel', x0 + 0.5, x1 - 0.5, ceil - 0.62, ceil - 0.30, cz0 + 0.5, cz0 + 1.05);
  for (let k = 0; k < 4; k++) {
    B.cyl('darkMetal', (x0 + x1) / 2, ceil - 0.28 - k * 0.06, cz1 - 0.75 - k * 0.09,
      0.035, 0.035, x1 - x0 - 1.0, 6, Math.PI / 2, 0);
  }
  // Ceiling light fittings, so the pools on the floor have somewhere to have come from.
  for (let i = 0; i < 5; i++) {
    const lx = 9.5 + i * 3.2;
    B.span('darkMetal', lx - 0.55, lx + 0.55, ceil - 0.40, ceil - 0.30, ((cz0 + cz1) / 2) - 0.22, ((cz0 + cz1) / 2) + 0.22);
    B.span('glassLit', lx - 0.48, lx + 0.48, ceil - 0.46, ceil - 0.40, ((cz0 + cz1) / 2) - 0.17, ((cz0 + cz1) / 2) + 0.17);
    B.source('arcade', lx, ceil - 0.55, (cz0 + cz1) / 2, 0xffd8ac, 0.9);
  }

  // Shopfront rhythm down both sides, set at the concourse edge with the bay depth behind.
  for (const z of [cz0, cz1]) {
    const inw = z === cz0 ? -1 : 1;   // from the concourse edge INTO the shop bay
    const rr = rngFor(`arcade.shops.${z}`);
    const n = 8;
    for (let i = 0; i < n; i++) {
      const a = x0 + (x1 - x0) * (i / n) + 0.18;
      const b = x0 + (x1 - x0) * ((i + 1) / n) - 0.18;
      rr.chance(0.72); rr.chance(0.7);   // keep the draw sequence stable
      const gz = z + inw * 0.34;          // glazing plane
      const bz = z + inw * 1.30;          // back of the shop interior
      const HEAD = 3.10;                  // shopfront head, raised for the 5.4 m ceiling

      // Glazing, split by a door opening. The door leaf is the point: a 0.9 x 2.1 m opening is the
      // only unambiguous 2 m scale datum in an interior frame, and the arcade had none.
      const dw = 0.95, dA = a + (b - a) * rr.range(0.15, 0.6), dB = dA + dw;
      B.pane('window', a, dA, 0.20, HEAD, gz, gz + inw * 0.05, `arc.${z.toFixed(1)}.${i}.a`);
      B.pane('window', dB, b, 0.20, HEAD, gz, gz + inw * 0.05, `arc.${z.toFixed(1)}.${i}.b`);
      B.pane('window', dA, dB, 2.15, HEAD, gz, gz + inw * 0.05, `arc.${z.toFixed(1)}.${i}.c`);
      B.span('darkMetal', dA - 0.05, dA, 0, HEAD, gz + inw * -0.03, gz + inw * 0.08);
      B.span('darkMetal', dB, dB + 0.05, 0, HEAD, gz + inw * -0.03, gz + inw * 0.08);
      B.span('glassDark', dA + 0.04, dB - 0.04, 0.02, 2.10, gz + inw * 0.16, gz + inw * 0.21);

      B.span('darkMetal', a, b, 0, 0.20, gz + inw * -0.04, gz + inw * 0.06);   // stallriser
      B.span('darkMetal', a, b, HEAD, HEAD + 0.17, gz + inw * -0.06, gz + inw * 0.08);
      // Fascia carcass above each unit, and System 4's lettering on it.
      B.span('painted', a - 0.18, b + 0.18, HEAD + 0.17, HEAD + 1.32, z + inw * 0.16, z + inw * 0.34);
      {
        const cs = SIGN_ATLAS.cells;
        const pick = rr.chance(0.55)
          ? cellsOfKind(cs, 'box') : cellsOfKind(cs, 'fascia').concat(cellsOfKind(cs, 'holo'));
        if (pick.length) {
          const c = pick[Math.floor(rr.f() * pick.length)];
          B.signQuad(a + 0.05, b - 0.05, HEAD + 0.30, HEAD + 1.18,
            z + inw * 0.14, z + inw * 0.155, c, 'arc.sign.' + z.toFixed(1) + '.' + i, 0.85, -inw);
        }
      }
      // Roller-shutter box
      if (rr.chance(0.35)) B.span('steel', a - 0.1, b + 0.1, HEAD + 0.05, HEAD + 0.42, gz + inw * 0.08, gz + inw * 0.30);
      // Pier between tenancies, full height to the fascia
      B.span('concrete', b - 0.02, b + 0.20, 0, HEAD + 1.32, z + inw * 0.14, gz + inw * 0.10);
      // Shop interior: back wall held off the glazing so there is somewhere for the eye to go.
      B.span('concreteB', a, b, 0, ceil, bz, bz + inw * 0.30);
      B.span('paintedWarm', a + 0.3, b - 0.3, 0, 0.95, bz + inw * -0.55, bz + inw * -0.15);
      for (let sy = 1.2; sy < HEAD - 0.5; sy += 0.6) {
        B.span('darkMetal', a + 0.2, b - 0.2, sy, sy + 0.05, bz + inw * -0.32, bz);
      }
    }
    // Wall behind the shops
    B.span('concreteB', x0, x1, 0, ceil, z + inw * 1.60, z + inw * 1.90);
    world.solid(x0, x1, Math.min(z, z + inw * 1.9), Math.max(z, z + inw * 1.9), 0, ceil, 'arcade.side');
  }

  // Floor: a tiled band pattern, different material from the street. Interior floors read as
  // interior because they are laid, not poured.
  const tiles = 46;
  for (let i = 0; i < tiles; i++) {
    const a = x0 + (x1 - x0) * (i / tiles), b = x0 + (x1 - x0) * ((i + 1) / tiles);
    const rr = rngFor(`arcade.tile.${i}`);
    B.span(rr.chance(0.5) ? 'pavement' : 'kerb', a + 0.01, b - 0.01, WORLD.KERB_H, WORLD.KERB_H + 0.012,
      cz0 + 0.42, cz1 - 0.42);
  }
}

function buildStair(B, world, st) {
  const { zLo, zHi, width, a, b, landA, landB, railH } = st;

  const flight = (f) => {
    const steps = 16;
    const dx = (f.x1 - f.x0) / steps;
    for (let i = 0; i < steps; i++) {
      const t0 = i / steps, t1 = (i + 1) / steps;
      const u0 = f.dir > 0 ? t0 : 1 - t0;
      const y0 = f.yLo + (f.yHi - f.yLo) * u0;
      const x = f.x0 + dx * (i + 0.5);
      // Tread (open steel grating) + riser gap. Open treads are why service stairs read as service.
      B.span('grate', x - Math.abs(dx) / 2, x + Math.abs(dx) / 2, y0 - 0.035, y0, zLo, zHi);
    }
    // Height of the flight at a given x. One function, used by the treads, the stringers, the
    // handrail and the balusters, so they cannot disagree about where the stair is.
    const yAt = (x) => {
      const t = (x - f.x0) / (f.x1 - f.x0);
      const u = f.dir > 0 ? t : 1 - t;
      return f.yLo + (f.yHi - f.yLo) * u;
    };

    // Stringers and handrail, built point-to-point. Round 6: these previously used a hand-derived
    // rotation angle with a sign correction that was wrong, which is why a critic reported the
    // handrail "terminates in mid-air showing an open circular section, connected to no post".
    for (const z of [zLo - 0.05, zHi + 0.05]) {
      B.rod('steel', [f.x0, yAt(f.x0) - 0.17, z], [f.x1, yAt(f.x1) - 0.17, z], 0.055, 4);
      B.rod('steel', [f.x0, yAt(f.x0) + railH, z], [f.x1, yAt(f.x1) + railH, z], 0.022, 6);
      B.rod('steel', [f.x0, yAt(f.x0) + railH * 0.52, z], [f.x1, yAt(f.x1) + railH * 0.52, z], 0.016, 5);
      const n = 8;
      for (let i = 0; i < n; i++) {
        const x = f.x0 + (f.x1 - f.x0) * ((i + 0.5) / n);
        B.cyl('steel', x, yAt(x) + railH / 2, z, 0.014, 0.014, railH, 5);
      }
      // Newel at each end, landing the rail on something instead of stopping in space.
      for (const x of [f.x0, f.x1]) {
        B.cyl('steel', x, yAt(x) + railH / 2, z, 0.026, 0.026, railH + 0.06, 6);
      }
    }
    world.ramp(f.x0, f.x1, zLo, zHi, f.yLo, f.yHi, 'x', f.dir, 'stair');
  };

  flight(a);
  flight(b);

  for (const L of [landA, landB]) {
    B.span('grate', L.x0, L.x1, L.y - 0.045, L.y, zLo, zHi);
    world.floor(L.x0, L.x1, zLo, zHi, L.y, 'stair.landing');
    for (const z of [zLo - 0.03, zHi + 0.03]) {
      B.cyl('steel', (L.x0 + L.x1) / 2, L.y + railH, z, 0.022, 0.022, L.x1 - L.x0, 6, Math.PI / 2, 0);
      for (let i = 0; i < 3; i++) {
        B.cyl('steel', L.x0 + (L.x1 - L.x0) * ((i + 0.5) / 3), L.y + railH / 2, z, 0.014, 0.014, railH, 5);
      }
    }
    // Support posts down to the ground
    for (const x of [L.x0 + 0.15, L.x1 - 0.15]) {
      B.cyl('steel', x, L.y / 2, zLo + 0.05, 0.05, 0.05, L.y, 6);
      B.cyl('steel', x, L.y / 2, zHi - 0.05, 0.05, 0.05, L.y, 6);
    }
  }
}

function buildWalkway(B, world) {
  const { x0, x1, z0, z1, y } = WALKWAY;

  // Deck
  B.span('grate', x0, x1, y - 0.07, y, z0, z1);
  world.floor(x0, x1, z0, z1, y, 'walkway');
  // Underside beams -- what you see when you are standing under it on the street.
  for (let z = z0; z <= z1; z += 1.8) {
    B.span('steel', x0, x1, y - 0.26, y - 0.07, z - 0.06, z + 0.06);
  }
  B.span('steel', x1 - 0.10, x1, y - 0.34, y - 0.07, z0, z1);
  B.span('steel', x0, x0 + 0.10, y - 0.34, y - 0.07, z0, z1);

  // Support structure.
  //
  // Round 5: a critic wrote "the deck is unsupported -- everything below the deck edge is void, no
  // columns, no cross-beams, no brackets, no hangers, at an implied 5-7 m above the road", and that
  // was accurate: the only thing here was a thin diagonal every 3.6 m. A 32 m cantilevered deck
  // needs to look like it is held up by something.
  for (let z = z0 + 1.4; z < z1; z += 4.2) {
    // Column down to the pavement at the deck's outer edge, with a base plate and a head bracket.
    B.cyl('steel', x1 - 0.14, (y - 0.30) / 2 + WORLD.KERB_H, z, 0.075, 0.095, y - 0.30 - WORLD.KERB_H, 8);
    B.span('darkMetal', x1 - 0.30, x1 + 0.02, WORLD.KERB_H, WORLD.KERB_H + 0.05, z - 0.18, z + 0.18);
    B.span('steel', x1 - 0.26, x1 - 0.02, y - 0.34, y - 0.26, z - 0.12, z + 0.12);
    // Knee brace from the column head back to the wall.
    const g = new THREE.CylinderGeometry(0.042, 0.042, 1.9, 6);
    const m = new THREE.Matrix4().makeRotationZ(-0.72);
    m.setPosition((x0 + x1) / 2 + 0.15, y - 0.86, z);
    g.applyMatrix4(m);
    B.add('steel', g);
    // Wall tie plate.
    B.span('steel', x0, x0 + 0.10, y - 1.55, y - 1.25, z - 0.14, z + 0.14);
    world.solid(x1 - 0.22, x1 - 0.06, z - 0.10, z + 0.10, WORLD.KERB_H, y - 0.26, 'walkway.col');
  }
  // Continuous edge beam, readable from the street below.
  B.span('steel', x1 - 0.16, x1, y - 0.42, y - 0.07, z0, z1);

  // Railing on the street side + a lower kick rail.
  for (const yy of [y + 1.05, y + 0.55]) {
    B.cyl('steel', (x1 + x0) / 2, yy, z1, 0.024, 0.024, 0.1, 5);
    const g = new THREE.CylinderGeometry(0.024, 0.024, z1 - z0, 6);
    const m = new THREE.Matrix4().makeRotationX(Math.PI / 2);
    m.setPosition(x1 - 0.03, yy, (z0 + z1) / 2);
    g.applyMatrix4(m);
    B.add('steel', g);
  }
  for (let z = z0; z <= z1; z += 1.35) {
    B.cyl('steel', x1 - 0.03, y + 0.55, z, 0.016, 0.016, 1.10, 5);
  }
  // Mesh infill panels -- reads as safety mesh at distance.
  for (let z = z0; z < z1; z += 1.35) {
    for (let k = 0; k < 4; k++) {
      B.cyl('steel', x1 - 0.03, y + 0.20 + k * 0.28, z + 0.675, 0.008, 0.008, 1.34, 4, Math.PI / 2, Math.PI / 2);
    }
  }
  // End caps so you cannot walk off into space.
  world.solid(x0, x1, z0 - 0.2, z0, y, y + 1.2, 'walkway.endA');
  world.solid(x0, x1, z1, z1 + 0.2, y, y + 1.2, 'walkway.endB');
  world.solid(x1, x1 + 0.12, z0, z1, y, y + 1.2, 'walkway.rail');
  B.span('steel', x0, x1, y, y + 1.15, z0 - 0.08, z0);
  B.span('steel', x0, x1, y, y + 1.15, z1, z1 + 0.08);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
function buildProps(B, world, props) {
  for (const p of props) {
    // Footprint for a contact shadow. A critic named the total absence of these as the single
    // largest tell: "every object reads as a decal pasted onto the ground plane. A photograph of a
    // night street is mostly occlusion; this set has none."
    const groundedBefore = B.grounded.length;
    const r = rngFor(`prop.${p.key}`);
    const y0 = p.walkway ? WALKWAY_Y
      : p.arcade ? WORLD.KERB_H
        : p.alley ? WORLD.KERB_H - 0.03
          : (Math.abs(p.x) <= WORLD.ROAD_HALF ? 0.02 : WORLD.KERB_H);
    const rot = p.rot;

    switch (p.kind) {
      // --- vehicles -------------------------------------------------------
      // Blocked-out but correctly proportioned: 4.4 x 1.8 x 1.45 saloon, 5.4 x 2.0 x 2.6 van,
      // 1.9 x 0.7 scooter. Sized from real vehicles because a car is the strongest scale datum on
      // a street after a person, and a wrong-sized one actively misleads the eye about everything
      // around it.
      case 'car': {
        // Overhaul 3A: archetype from the pool (8 bodies built once, clones placed), paint tint
        // baked as vertex colors. Collision keeps the caller's dimensions.
        const L = 4.4, W = 1.82, roofY = 1.46;
        const parts = tintPaint(vehicleFromPool(r.int(0, 5), L, W, roofY), r.pick(PAINTS));
        placeParts(parts, { rot: [0, rot, 0] });
        placeParts(parts, { pos: [p.x, y0, p.z] });
        B.addParts(parts);
        world.solid(p.x - W / 2, p.x + W / 2, p.z - L / 2, p.z + L / 2, y0, y0 + roofY, 'car');
        break;
      }
      case 'van': {
        const L = 5.4, W = 2.0, H = 2.55;
        const parts = tintPaint(vehicleFromPool(r.int(6, 7), L, W, H), r.pick(PAINTS));
        placeParts(parts, { rot: [0, rot, 0] });
        placeParts(parts, { pos: [p.x, y0, p.z] });
        B.addParts(parts);
        world.solid(p.x - W / 2, p.x + W / 2, p.z - L / 2, p.z + L / 2, y0, y0 + H, 'van');
        break;
      }
      case 'scooter': {
        const L = 1.9;
        B.box('painted', p.x, y0 + 0.62, p.z, 0.34, 0.30, L * 0.55, rot);
        B.box('darkMetal', p.x, y0 + 0.86, p.z - L * 0.22, 0.30, 0.22, 0.34, rot);
        B.cyl('steel', p.x, y0 + 1.02, p.z - L * 0.30, 0.02, 0.02, 0.56, 6, Math.PI / 2, rot);
        for (const dz of [-L * 0.36, L * 0.36]) {
          B.cyl('darkMetal', p.x, y0 + 0.26, p.z + dz, 0.26, 0.26, 0.10, 12, Math.PI / 2, 0);
        }
        B.cyl('steel', p.x + 0.16, y0 + 0.20, p.z + L * 0.20, 0.015, 0.015, 0.40, 5, 0.5, 0);
        world.solid(p.x - 0.35, p.x + 0.35, p.z - L / 2, p.z + L / 2, y0, y0 + 1.15, 'scooter');
        break;
      }

      // --- arcade / street furniture ---------------------------------------
      case 'vending': {
        const w = 1.02, d = 0.78, hh = 1.90;
        B.box('painted', p.x, y0 + hh / 2, p.z, w, hh, d, rot);
        B.box('glassLit', p.x, y0 + hh * 0.62, p.z + d / 2 * (Math.cos(rot) > 0 ? 1 : -1),
          w * 0.72, hh * 0.52, 0.05, rot);
        B.box('darkMetal', p.x, y0 + hh * 0.24, p.z + d / 2 * (Math.cos(rot) > 0 ? 1 : -1),
          w * 0.60, 0.18, 0.06, rot);
        B.box('darkMetal', p.x, y0 + hh + 0.03, p.z, w + 0.04, 0.06, d + 0.04, rot);
        world.solid(p.x - w / 2, p.x + w / 2, p.z - d / 2, p.z + d / 2, y0, y0 + hh, 'vending');
        break;
      }
      case 'aboard': {
        // A-board: two leaning panels, 0.6 x 0.9.
        for (const s of [-1, 1]) {
          B.box('paintedWarm', p.x + s * 0.13, y0 + 0.46, p.z, 0.62, 0.90, 0.03, rot + s * 0.16);
        }
        world.solid(p.x - 0.3, p.x + 0.3, p.z - 0.28, p.z + 0.28, y0, y0 + 0.92, 'aboard');
        break;
      }
      case 'bikerack': {
        const n = r.int(3, 6);
        for (let i = 0; i < n; i++) {
          const zz = p.z + (i - n / 2) * 0.42;
          // Sheffield hoop
          B.cyl('steel', p.x, y0 + 0.36, zz, 0.022, 0.022, 0.72, 6);
          B.cyl('steel', p.x + 0.62, y0 + 0.36, zz, 0.022, 0.022, 0.72, 6);
          B.cyl('steel', p.x + 0.31, y0 + 0.72, zz, 0.022, 0.022, 0.62, 6, 0, Math.PI / 2);
          if (r.chance(0.62)) {
            // A parked bicycle. Round 6: the previous version was two discs and a rod, which a
            // critic read as "black ellipsoids with one straight rod through, no frame, no fork,
            // no bars". Built now from the actual members, at real dimensions: 0.66 m wheels on a
            // 1.03 m wheelbase, so it reads as a bicycle rather than as debris.
            const cx = p.x + 0.31, lean = r.range(-0.10, 0.10);
            const R = 0.33, WB = 1.03;
            const xr = cx - WB / 2, xf = cx + WB / 2;      // rear / front hub
            const zr = zz + lean * 0.5, zf = zz - lean * 0.5;
            for (const [wx, wz] of [[xr, zr], [xf, zf]]) {
              B.cyl('darkMetal', wx, y0 + R, wz, R, R, 0.032, 16, Math.PI / 2, Math.PI / 2);
              B.cyl('steel', wx, y0 + R, wz, 0.055, 0.055, 0.05, 8, Math.PI / 2, Math.PI / 2);
            }
            const bb = [cx - 0.09, y0 + 0.30, zz];          // bottom bracket
            const st = [cx - 0.02, y0 + 0.78, zz];          // seat cluster
            const hd = [xf - 0.14, y0 + 0.86, zf];          // head tube top
            const m = r.pick(['painted', 'paintedWarm', 'darkMetal']);
            B.rod(m, [xr, y0 + R, zr], bb, 0.022, 5);       // chain stay
            B.rod(m, [xr, y0 + R, zr], st, 0.020, 5);       // seat stay
            B.rod(m, bb, st, 0.024, 5);                     // seat tube
            B.rod(m, bb, hd, 0.026, 5);                     // down tube
            B.rod(m, st, hd, 0.024, 5);                     // top tube
            B.rod('steel', hd, [xf, y0 + R, zf], 0.020, 5); // fork
            B.rod('darkMetal', [hd[0], hd[1] + 0.10, hd[2] - 0.22],
              [hd[0], hd[1] + 0.10, hd[2] + 0.22], 0.014, 5);      // handlebar
            B.box('darkMetal', st[0] - 0.02, st[1] + 0.09, st[2], 0.22, 0.05, 0.10, 0); // saddle
          }
        }
        world.solid(p.x - 0.1, p.x + 0.75, p.z - n * 0.22, p.z + n * 0.22, y0, y0 + 0.76, 'bikerack');
        break;
      }
      case 'signpost': {
        const h = 2.45;
        B.cyl('steel', p.x, y0 + h / 2, p.z, 0.035, 0.045, h, 8);
        B.cyl('steel', p.x, y0 + 0.04, p.z, 0.09, 0.11, 0.08, 10);
        const n = r.int(1, 2);
        for (let i = 0; i < n; i++) {
          const yy = y0 + h - 0.22 - i * 0.42;
          B.box('painted', p.x + 0.20, yy, p.z, 0.42, 0.30, 0.03, rot + r.jitter(0.12));
        }
        world.solid(p.x - 0.08, p.x + 0.08, p.z - 0.08, p.z + 0.08, y0, y0 + h, 'signpost');
        break;
      }
      case 'sacks': {
        // A pile of refuse sacks -- soft, overlapping, uneven. Cheap and very effective at
        // breaking the hard-edged-boxes read of a blockout.
        const n = r.int(4, 8);
        let baseY = y0;
        for (let i = 0; i < n; i++) {
          const rad = r.range(0.22, 0.34);
          const ox = r.jitter(0.34), oz = r.jitter(0.26);
          const yy = baseY + rad * (i > n / 2 ? 1.4 : 0.9);
          B.cyl('plastic', p.x + ox, yy, p.z + oz, rad * 0.72, rad, rad * 1.5, 8, r.jitter(0.5), r.range(0, 3));
        }
        world.solid(p.x - 0.5, p.x + 0.5, p.z - 0.4, p.z + 0.4, y0, y0 + 0.85, 'sacks');
        break;
      }
      case 'gasbottle': {
        const n = r.int(2, 4);
        for (let i = 0; i < n; i++) {
          const ox = r.jitter(0.28), oz = r.jitter(0.2);
          B.cyl('painted', p.x + ox, y0 + 0.44, p.z + oz, 0.16, 0.17, 0.88, 10);
          B.cyl('steel', p.x + ox, y0 + 0.92, p.z + oz, 0.06, 0.06, 0.10, 8);
          B.cyl('darkMetal', p.x + ox, y0 + 0.99, p.z + oz, 0.13, 0.13, 0.05, 10);
        }
        world.solid(p.x - 0.35, p.x + 0.35, p.z - 0.3, p.z + 0.3, y0, y0 + 1.0, 'gasbottle');
        break;
      }
      case 'walklamp': {
        // Deck light on the elevated walkway. Post + head, so the pool of light on the deck has a
        // fixture to have come from.
        const h = 2.9;
        B.cyl('steel', p.x, y0 + h / 2, p.z, 0.035, 0.05, h, 8);
        B.cyl('darkMetal', p.x, y0 + 0.05, p.z, 0.10, 0.12, 0.10, 10);
        B.box('darkMetal', p.x + 0.14, y0 + h - 0.06, p.z, 0.42, 0.12, 0.24, rot);
        B.box('glassLit', p.x + 0.14, y0 + h - 0.14, p.z, 0.36, 0.05, 0.20, rot);
        B.source('walklamp', p.x + 0.14, y0 + h - 0.22, p.z, 0xdce8ff, 0.85);
        world.solid(p.x - 0.08, p.x + 0.08, p.z - 0.08, p.z + 0.08, y0, y0 + h, 'walklamp');
        break;
      }
      case 'bollard': {
        const h = r.range(0.85, 1.05);
        B.cyl('darkMetal', p.x, y0 + h / 2, p.z, 0.06, 0.075, h, 8);
        B.cyl('steel', p.x, y0 + h, p.z, 0.075, 0.075, 0.05, 8);
        world.solid(p.x - 0.09, p.x + 0.09, p.z - 0.09, p.z + 0.09, y0, y0 + h, 'bollard');
        break;
      }
      case 'utilbox': {
        const w = r.range(0.55, 0.95), d = r.range(0.35, 0.55), h = r.range(1.0, 1.5);
        B.box('painted', p.x, y0 + h / 2, p.z, w, h, d, rot);
        B.box('darkMetal', p.x, y0 + h + 0.03, p.z, w + 0.05, 0.06, d + 0.05, rot);
        B.box('grate', p.x, y0 + h * 0.65, p.z + d / 2, w * 0.6, h * 0.25, 0.02, rot);
        world.solid(p.x - w / 2, p.x + w / 2, p.z - d / 2, p.z + d / 2, y0, y0 + h, 'utilbox');
        break;
      }
      case 'crates': case 'stack': {
        const n = r.int(2, 4);
        let y = y0;
        for (let i = 0; i < n; i++) {
          const w = r.range(0.45, 0.8), d = r.range(0.4, 0.7), h = r.range(0.3, 0.5);
          B.box(r.chance(0.5) ? 'plastic' : 'paintedWarm', p.x + r.jitter(0.12), y + h / 2, p.z + r.jitter(0.12),
            w, h, d, rot + r.jitter(0.3));
          y += h;
        }
        world.solid(p.x - 0.45, p.x + 0.45, p.z - 0.4, p.z + 0.4, y0, y, 'crates');
        break;
      }
      case 'bin': {
        const h = r.range(0.85, 1.15), rad = r.range(0.24, 0.34);
        B.cyl('darkMetal', p.x, y0 + h / 2, p.z, rad, rad * 0.92, h, 10);
        B.cyl('steel', p.x, y0 + h + 0.02, p.z, rad + 0.03, rad + 0.03, 0.05, 10);
        world.solid(p.x - rad, p.x + rad, p.z - rad, p.z + rad, y0, y0 + h, 'bin');
        break;
      }
      case 'pole': {
        // A street lighting column. Round S3: this was a bare shaft with a cross-arm and no
        // luminaire, so the street had no motivated source at all. Shaft tapers 0.11 -> 0.076 m,
        // which is a real column section; the previous 0.16 m read as a bollard on stilts.
        const h = r.range(4.6, 6.2);
        B.cyl('darkMetal', p.x, y0 + h / 2, p.z, 0.038, 0.055, h, 8);
        B.cyl('darkMetal', p.x, y0 + 0.05, p.z, 0.105, 0.125, 0.10, 10);       // base flange
        B.span('steel', p.x - 0.085, p.x + 0.085, y0 + 0.35, y0 + 0.95, p.z - 0.05, p.z + 0.05);
        // Outreach arm and lantern, leaning out over the roadway.
        const dir = p.x < 0 ? 1 : -1;
        const armEnd = [p.x + dir * 0.95, y0 + h + 0.16, p.z];
        B.rod('darkMetal', [p.x, y0 + h - 0.25, p.z], armEnd, 0.030, 6);
        B.span('darkMetal', armEnd[0] - 0.30, armEnd[0] + 0.30, armEnd[1] - 0.14, armEnd[1] + 0.04,
          p.z - 0.17, p.z + 0.17);
        B.span('glassLit', armEnd[0] - 0.25, armEnd[0] + 0.25, armEnd[1] - 0.17, armEnd[1] - 0.13,
          p.z - 0.13, p.z + 0.13);
        B.source('streetlamp', armEnd[0], armEnd[1] - 0.20, p.z, 0xffd6a0, 1.0);
        world.solid(p.x - 0.09, p.x + 0.09, p.z - 0.09, p.z + 0.09, y0, y0 + h, 'pole');
        break;
      }
      case 'hydrant': {
        B.cyl('paintedWarm', p.x, y0 + 0.34, p.z, 0.10, 0.13, 0.68, 8);
        B.cyl('paintedWarm', p.x, y0 + 0.72, p.z, 0.09, 0.11, 0.12, 8);
        B.cyl('paintedWarm', p.x, y0 + 0.42, p.z, 0.055, 0.055, 0.36, 6, Math.PI / 2, 0);
        world.solid(p.x - 0.15, p.x + 0.15, p.z - 0.15, p.z + 0.15, y0, y0 + 0.8, 'hydrant');
        break;
      }
      case 'dumpster': {
        const w = r.range(1.6, 2.2), d = r.range(0.95, 1.25), h = r.range(1.05, 1.35);
        B.box('painted', p.x, y0 + h / 2, p.z, w, h, d, rot);
        B.box('darkMetal', p.x, y0 + h + 0.04, p.z, w * 1.02, 0.08, d * 1.02, rot);
        for (const s of [-1, 1]) {
          B.cyl('darkMetal', p.x + s * w * 0.38, y0 + 0.09, p.z + d * 0.42, 0.09, 0.09, 0.06, 8, Math.PI / 2, rot);
        }
        world.solid(p.x - w / 2, p.x + w / 2, p.z - d / 2, p.z + d / 2, y0, y0 + h, 'dumpster');
        break;
      }
      case 'condenser': {
        const w = r.range(0.8, 1.2), d = r.range(0.5, 0.8), h = r.range(0.7, 1.0);
        B.box('plastic', p.x, y0 + h / 2 + 0.12, p.z, w, h, d, rot);
        B.box('grate', p.x, y0 + h / 2 + 0.12, p.z + d / 2, w * 0.8, h * 0.7, 0.03, rot);
        for (const s of [-1, 1]) B.span('steel', p.x + s * w * 0.4 - 0.03, p.x + s * w * 0.4 + 0.03, y0, y0 + 0.12, p.z - 0.03, p.z + 0.03);
        world.solid(p.x - w / 2, p.x + w / 2, p.z - d / 2, p.z + d / 2, y0, y0 + h + 0.12, 'condenser');
        break;
      }
      case 'pallet': {
        const n = r.int(1, 3);
        for (let i = 0; i < n; i++) {
          B.box('paintedWarm', p.x, y0 + 0.06 + i * 0.14, p.z, 1.15, 0.11, 0.85, rot + i * 0.06);
        }
        world.solid(p.x - 0.6, p.x + 0.6, p.z - 0.45, p.z + 0.45, y0, y0 + 0.06 + n * 0.14, 'pallet');
        break;
      }
      case 'drum': {
        const n = r.int(1, 3);
        for (let i = 0; i < n; i++) {
          const ox = r.jitter(0.5), oz = r.jitter(0.3);
          B.cyl(r.chance(0.5) ? 'painted' : 'darkMetal', p.x + ox, y0 + 0.44, p.z + oz, 0.29, 0.29, 0.88, 12);
          for (const yy of [0.24, 0.64]) {
            B.cyl('steel', p.x + ox, y0 + yy, p.z + oz, 0.305, 0.305, 0.04, 12);
          }
        }
        world.solid(p.x - 0.4, p.x + 0.4, p.z - 0.35, p.z + 0.35, y0, y0 + 0.9, 'drum');
        break;
      }
      case 'manhole': {
        B.cyl('darkMetal', p.x, 0.055, p.z, 0.34, 0.34, 0.03, 16, 0, rot);
        B.cyl('steel', p.x, 0.048, p.z, 0.38, 0.38, 0.02, 16, 0, rot);
        break;
      }
      case 'drain': {
        if (r.chance(0.45)) B.vents.push({ x: p.x, y: 0.08, z: p.z, scale: 0.72 });
        B.grounded.push(null);   // flat fixture, no contact shadow
        B.span('grate', p.x - 0.20, p.x + 0.20, 0.0, 0.022, p.z - 0.34, p.z + 0.34);
        B.span('kerb', p.x - 0.24, p.x + 0.24, 0.0, 0.028, p.z - 0.40, p.z - 0.34);
        B.span('kerb', p.x - 0.24, p.x + 0.24, 0.0, 0.028, p.z + 0.34, p.z + 0.40);
        break;
      }
    }
    if (B.grounded.length === groundedBefore) {
      const RAD = {
        car: 2.4, van: 2.9, scooter: 1.0, vending: 0.75, aboard: 0.45, bikerack: 0.9,
        signpost: 0.22, sacks: 0.60, gasbottle: 0.40, walklamp: 0.20, dumpster: 1.3,
        crates: 0.55, stack: 0.55, bin: 0.40, condenser: 0.65, pallet: 0.70, drum: 0.45,
        bollard: 0.16, utilbox: 0.55, pole: 0.20, hydrant: 0.22,
      };
      const rad = RAD[p.kind];
      if (rad) B.grounded.push({ x: p.x, z: p.z, y: y0 + 0.012, r: rad });
    }
  }
}

function buildCables(B, cables) {
  for (const c of cables) {
    const a = new THREE.Vector3(...c.a);
    const b = new THREE.Vector3(...c.b);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    // Catenary approximated with a quadratic sag -- straight lines between buildings are one of the
    // fastest ways to make a street read as CG.
    const q1 = a.clone().lerp(b, 0.25); q1.y -= c.sag * 0.75;
    const q2 = mid.clone(); q2.y -= c.sag;
    const q3 = a.clone().lerp(b, 0.75); q3.y -= c.sag * 0.75;
    const curve = new THREE.CatmullRomCurve3([a, q1, q2, q3, b]);
    B.tube('cable', curve, c.rad, 12, 4);
  }
}

// ---------------------------------------------------------------------------
// Distant backdrop -- the city has to continue past the block you can walk.
// ---------------------------------------------------------------------------
function buildBackdrop(B) {
  const r = rngFor('backdrop');
  for (let i = 0; i < 190; i++) {
    const ang = r.range(0, Math.PI * 2);
    const dist = r.range(52, 230);
    const x = Math.cos(ang) * dist;
    const z = Math.sin(ang) * dist * 1.35;
    if (Math.abs(x) < 34 && Math.abs(z) < 60) continue;   // do not block the playable street
    const w = r.range(9, 34), d = r.range(9, 34);
    const h = r.range(16, 30) + (dist / 230) * r.range(10, 78);
    const lit = r.chance(0.42);
    B.span(lit ? 'backdropLit' : 'backdrop', x - w / 2, x + w / 2, 0, h, z - d / 2, z + d / 2);
    // Roof mass so the skyline is not a row of flat tops.
    if (r.chance(0.5)) {
      B.span('backdrop', x - w * 0.25, x + w * 0.25, h, h + r.range(2, 9), z - d * 0.25, z + d * 0.25);
    }
    if (r.chance(0.3)) {
      B.cyl('backdrop', x + r.jitter(w * 0.3), h + r.range(3, 12), z + r.jitter(d * 0.3), 0.25, 0.4, r.range(6, 24), 5);
    }
  }
}

// ---------------------------------------------------------------------------
export function buildCity(scene, world, plan, materials) {
  const B = new Builder(materials);

  buildGround(B, world);
  for (const p of plan.parcels) buildParcel(B, world, p);
  buildAlleys(B, world);
  buildArcade(B, world);
  buildStair(B, world, plan.stair);
  buildWalkway(B, world);
  buildProps(B, world, plan.props);
  buildCables(B, plan.cables);
  buildBackdrop(B);

  const meshes = B.finish(scene);
  return { meshes, pieces: B.count, buckets: B.buckets.size, sources: B.sources,
    vents: B.vents || [], grounded: (B.grounded || []).filter(Boolean) };
}
